/**
 * Generic Object List View
 *
 * Displays records for any object type in a list format.
 * Works with both configured list views and auto-generated default list views.
 */

import React, { useState, useMemo } from 'react';
import { Plus, Search, X, Filter, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { ListViewTable } from './ListViewTable';
import { Pagination } from './Pagination';
import { GenericRecordModal } from '../modals/GenericRecordModal';
import {
  useGenericListViewData,
  useGenericListViews
} from '../../services/queries/genericListViewQueries';
import { useDeleteGenericRecord } from '../../services/queries/genericListViewQueries';
import type { ListViewRecord } from '../../types/listView';
import { useToast } from '../../contexts/ToastContext';

interface GenericObjectListViewProps {
  objectApiName: string;
  objectId: string;
  objectLabel: string;
  onNavigateToRecord?: (recordId: string) => void;
  onCreateRecord?: () => void;
}

export const GenericObjectListView: React.FC<GenericObjectListViewProps> = ({
  objectApiName,
  objectId,
  objectLabel,
  onNavigateToRecord,
  onCreateRecord
}) => {
  const { success, error: showError } = useToast();
  
  // State for search
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for sorting (multi-column support)
  const [sortConfig, setSortConfig] = useState<{
    columns: { field: string; direction: 'asc' | 'desc' }[];
  }>({ columns: [] });
  
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // State for record modal
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string | undefined>(undefined);
  
  // State for bulk selection
  const [selectedRecordIds, setSelectedRecordIds] = useState<Set<string>>(new Set());
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  
  // State for advanced filters
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filters, setFilters] = useState<{
    conditions: Array<{
      field: string;
      operator: 'equals' | 'contains' | 'startsWith' | 'greaterThan' | 'lessThan' | 'between';
      value: any;
      value2?: any;
    }>;
    logic: 'AND' | 'OR';
  }>({ conditions: [], logic: 'AND' });
  
  // Fetch available list views (will return default if none configured)
  const { data: listViews } = useGenericListViews(objectApiName, objectId);
  
  // Get default list view
  const defaultListView = listViews?.[0];
  
  // Fetch data for default list view
  const { data: listViewData, isLoading: loadingData, isFetching, isPlaceholderData, error: listViewError, refetch: refetchListViewData } = useGenericListViewData(
    objectApiName,
    objectId,
    defaultListView?.id,
    { page: currentPage - 1, pageSize }
  );
  const isFetchingPage = isFetching && isPlaceholderData;
  
  // Log errors for debugging
  React.useEffect(() => {
    if (listViewError) {
      console.error('[GenericObjectListView] Error loading list view data:', listViewError);
    }
    if (listViewData) {
      console.log('[GenericObjectListView] List view data loaded:', listViewData);
    }
  }, [listViewError, listViewData]);
  
  // Delete mutation
  const deleteMutation = useDeleteGenericRecord(objectApiName);
  
  // Handle record click
  const handleRecordClick = (record: ListViewRecord) => {
    if (onNavigateToRecord) {
      onNavigateToRecord(record.id);
    } else {
      // Open edit modal if no navigation handler provided
      setSelectedRecordId(record.id);
      setIsRecordModalOpen(true);
    }
  };
  
  // Handle create button click
  const handleCreateClick = () => {
    if (onCreateRecord) {
      onCreateRecord();
    } else {
      setSelectedRecordId(undefined);
      setIsRecordModalOpen(true);
    }
  };
  
  // Handle delete button click (used by ListViewTable)
  const handleDeleteClick = async (recordId: string) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }
    
    try {
      await deleteMutation.mutateAsync(recordId);
      success('Record Deleted', 'The record has been deleted successfully');
    } catch (err: any) {
      showError('Error', err?.message || 'Failed to delete record');
    }
  };
  
  // Filter records based on search and advanced filters
  const filteredRecords = useMemo(() => {
    if (!listViewData?.records) return [];
    
    let filtered = listViewData.records;
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const visibleColumns = listViewData.listView.columns.filter(col => col.isVisible);
      
      filtered = filtered.filter(record => {
        return visibleColumns.some(col => {
          const value = String(record[col.fieldApiName] || '').toLowerCase();
          return value.includes(query);
        });
      });
    }
    
    // Apply advanced filters
    if (filters.conditions.length > 0) {
      filtered = filtered.filter(record => {
        const results = filters.conditions.map(condition => {
          const fieldValue = record[condition.field];
          const fieldColumn = listViewData.listView.columns.find(col => col.fieldApiName === condition.field);
          
          switch (condition.operator) {
            case 'equals':
              return String(fieldValue || '') === String(condition.value || '');
            case 'contains':
              return String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
            case 'startsWith':
              return String(fieldValue || '').toLowerCase().startsWith(String(condition.value || '').toLowerCase());
            case 'greaterThan':
              return Number(fieldValue) > Number(condition.value);
            case 'lessThan':
              return Number(fieldValue) < Number(condition.value);
            case 'between':
              const numValue = Number(fieldValue);
              return numValue >= Number(condition.value) && numValue <= Number(condition.value2 || condition.value);
            default:
              return true;
          }
        });
        
        return filters.logic === 'AND' 
          ? results.every(r => r)
          : results.some(r => r);
      });
    }
    
    return filtered;
  }, [listViewData, searchQuery, filters]);
  
  // Apply sorting to filtered records (multi-column support)
  const sortedRecords = useMemo(() => {
    if (!sortConfig.columns || sortConfig.columns.length === 0) return filteredRecords;
    
    return [...filteredRecords].sort((a, b) => {
      for (const sortCol of sortConfig.columns) {
        const aValue = a[sortCol.field];
        const bValue = b[sortCol.field];
        
        if (aValue === bValue) continue;
        if (aValue == null) return 1;
        if (bValue == null) return -1;
        
        const comparison = aValue < bValue ? -1 : 1;
        const result = sortCol.direction === 'asc' ? comparison : -comparison;
        if (result !== 0) return result;
      }
      return 0;
    });
  }, [filteredRecords, sortConfig]);
  
  // Calculate pagination
  const totalRecords = listViewData?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const recordsToShow = sortedRecords;
  
  // Reset to page 1 when search or filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters]);
  
  // Handle record selection
  const handleSelectRecord = (recordId: string, checked: boolean) => {
    setSelectedRecordIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(recordId);
      } else {
        next.delete(recordId);
      }
      return next;
    });
  };
  
  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRecordIds(new Set(sortedRecords.map(r => r.id)));
    } else {
      setSelectedRecordIds(new Set());
    }
  };
  
  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedRecordIds.size === 0) return;
    
    const count = selectedRecordIds.size;
    if (!window.confirm(`Are you sure you want to delete ${count} ${count === 1 ? 'record' : 'records'}?`)) {
      return;
    }
    
    try {
      const deletePromises = Array.from(selectedRecordIds).map(id => 
        deleteMutation.mutateAsync(id)
      );
      await Promise.all(deletePromises);
      setSelectedRecordIds(new Set());
      success('Bulk Delete Complete', `Successfully deleted ${count} ${count === 1 ? 'record' : 'records'}`);
    } catch (err: any) {
      showError('Bulk Delete Failed', err?.message || 'Failed to delete some records');
    }
  };
  
  // Bulk update
  const handleBulkUpdate = () => {
    if (selectedRecordIds.size === 0) return;
    setShowBulkUpdateModal(true);
  };
  
  const handleBulkUpdateConfirm = async (fieldApiName: string, value: any) => {
    if (selectedRecordIds.size === 0) return;
    
    try {
      // TODO: Implement bulk update API call
      // For now, show success message
      success('Bulk Update Complete', `Updated ${selectedRecordIds.size} ${selectedRecordIds.size === 1 ? 'record' : 'records'}`);
      setShowBulkUpdateModal(false);
      setSelectedRecordIds(new Set());
    } catch (err: any) {
      showError('Bulk Update Failed', err?.message || 'Failed to update records');
    }
  };
  
  // Export to CSV
  const handleExportCSV = () => {
    if (!listViewData || sortedRecords.length === 0) {
      showError('Export Failed', 'No records to export');
      return;
    }
    
    const visibleColumns = listViewData.listView.columns
      .filter(col => col.isVisible)
      .sort((a, b) => a.order - b.order);
    
    // Create CSV header
    const headers = visibleColumns.map(col => col.fieldLabel);
    const csvRows = [headers.join(',')];
    
    // Add data rows
    sortedRecords.forEach(record => {
      const row = visibleColumns.map(col => {
        let value = record[col.fieldApiName];
        
        // Format value based on type
        if (value === null || value === undefined) {
          value = '';
        } else if (col.fieldType === 'date' || col.fieldType === 'datetime') {
          value = value instanceof Date ? value.toISOString() : String(value);
        } else if (typeof value === 'string') {
          // Escape quotes and wrap in quotes if contains comma or newline
          value = value.replace(/"/g, '""');
          if (value.includes(',') || value.includes('\n') || value.includes('"')) {
            value = `"${value}"`;
          }
        } else {
          value = String(value);
        }
        
        return value;
      });
      csvRows.push(row.join(','));
    });
    
    // Create blob and download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${objectLabel}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    success('Export Complete', `Exported ${sortedRecords.length} records to CSV (current page)`);
  };
  
  // Handle sort - supports multi-column sorting with Shift+click
  const handleSort = (field: string, event?: React.MouseEvent) => {
    const isShiftClick = event?.shiftKey || false;
    
    setSortConfig(prev => {
      const existingIndex = prev.columns.findIndex(col => col.field === field);
      
      if (existingIndex >= 0) {
        const existingCol = prev.columns[existingIndex];
        if (existingCol.direction === 'asc') {
          // Toggle to desc
          const newColumns = [...prev.columns];
          newColumns[existingIndex] = { ...existingCol, direction: 'desc' };
          return { columns: newColumns };
        } else {
          // Remove from sort
          const newColumns = prev.columns.filter((_, idx) => idx !== existingIndex);
          return { columns: newColumns };
        }
      } else {
        // New field
        if (isShiftClick && prev.columns.length > 0) {
          // Add as secondary sort
          return { columns: [...prev.columns, { field, direction: 'asc' }] };
        } else {
          // Replace with new primary sort
          return { columns: [{ field, direction: 'asc' }] };
        }
      }
    });
  };
  
  if (loadingData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading records...</p>
        </div>
      </div>
    );
  }
  
  if (!listViewData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">
          {listViewError ? (
            <>
              Error loading records: {listViewError instanceof Error ? listViewError.message : String(listViewError)}
              <br />
              <span className="text-xs text-gray-500 mt-2 block">Check console for details</span>
            </>
          ) : (
            'No list view data available.'
          )}
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {objectLabel} Records
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
            {selectedRecordIds.size > 0 && (
              <span className="ml-2 text-purple-600 dark:text-purple-400">
                • {selectedRecordIds.size} selected
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {sortedRecords.length > 0 && (
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          )}
          <button
            onClick={handleCreateClick}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New {objectLabel}
          </button>
        </div>
      </div>
      
      {/* Search and Filter Bar */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={`Search ${objectLabel.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className={`px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 ${
              filters.conditions.length > 0 ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : ''
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {filters.conditions.length > 0 && (
              <span className="px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">
                {filters.conditions.length}
              </span>
            )}
            {showFilterPanel ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>
        
        {/* Advanced Filter Panel */}
        {showFilterPanel && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Advanced Filters</h3>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600 dark:text-gray-400">Logic:</label>
                <select
                  value={filters.logic}
                  onChange={(e) => setFilters(prev => ({ ...prev, logic: e.target.value as 'AND' | 'OR' }))}
                  className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700"
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
                {filters.conditions.length > 0 && (
                  <button
                    onClick={() => setFilters({ conditions: [], logic: 'AND' })}
                    className="text-xs text-red-600 dark:text-red-400 hover:underline"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              {filters.conditions.map((condition, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <select
                    value={condition.field}
                    onChange={(e) => {
                      const newConditions = [...filters.conditions];
                      newConditions[index] = { ...condition, field: e.target.value };
                      setFilters(prev => ({ ...prev, conditions: newConditions }));
                    }}
                    className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700"
                  >
                    <option value="">Select field...</option>
                    {listViewData?.listView.columns.filter(col => col.isVisible).map(col => (
                      <option key={col.fieldApiName} value={col.fieldApiName}>
                        {col.fieldLabel}
                      </option>
                    ))}
                  </select>
                  
                  <select
                    value={condition.operator}
                    onChange={(e) => {
                      const newConditions = [...filters.conditions];
                      newConditions[index] = { ...condition, operator: e.target.value as any };
                      setFilters(prev => ({ ...prev, conditions: newConditions }));
                    }}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700"
                  >
                    <option value="equals">Equals</option>
                    <option value="contains">Contains</option>
                    <option value="startsWith">Starts With</option>
                    <option value="greaterThan">Greater Than</option>
                    <option value="lessThan">Less Than</option>
                    <option value="between">Between</option>
                  </select>
                  
                  <input
                    type="text"
                    value={condition.value || ''}
                    onChange={(e) => {
                      const newConditions = [...filters.conditions];
                      newConditions[index] = { ...condition, value: e.target.value };
                      setFilters(prev => ({ ...prev, conditions: newConditions }));
                    }}
                    placeholder="Value"
                    className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700"
                  />
                  
                  {condition.operator === 'between' && (
                    <input
                      type="text"
                      value={condition.value2 || ''}
                      onChange={(e) => {
                        const newConditions = [...filters.conditions];
                        newConditions[index] = { ...condition, value2: e.target.value };
                        setFilters(prev => ({ ...prev, conditions: newConditions }));
                      }}
                      placeholder="To"
                      className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700"
                    />
                  )}
                  
                  <button
                    onClick={() => {
                      const newConditions = filters.conditions.filter((_, idx) => idx !== index);
                      setFilters(prev => ({ ...prev, conditions: newConditions }));
                    }}
                    className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              <button
                onClick={() => {
                  setFilters(prev => ({
                    ...prev,
                    conditions: [...prev.conditions, { field: '', operator: 'equals', value: '' }]
                  }));
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm text-gray-700 dark:text-gray-300"
              >
                + Add Filter Condition
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Bulk Action Toolbar */}
      {selectedRecordIds.size > 0 && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
              {selectedRecordIds.size} {selectedRecordIds.size === 1 ? 'record' : 'records'} selected
            </span>
            <button
              onClick={() => setSelectedRecordIds(new Set())}
              className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkUpdate}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm transition-colors"
            >
              Bulk Update
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm transition-colors"
            >
              Delete Selected
            </button>
          </div>
        </div>
      )}
      
      {/* Records Table */}
      {recordsToShow.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          {searchQuery ? (
            <>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                No records found matching "{searchQuery}"
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="text-purple-600 dark:text-purple-400 hover:underline"
              >
                Clear search
              </button>
            </>
          ) : (
            <>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                No {objectLabel.toLowerCase()} records yet.
              </p>
              <button
                onClick={handleCreateClick}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create First Record
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="relative">
            <ListViewTable
              listView={listViewData.listView}
              records={recordsToShow}
              onRecordClick={handleRecordClick}
              isLoading={loadingData}
              sortConfig={sortConfig}
              onSort={handleSort}
              selectedRecordIds={selectedRecordIds}
              onSelectRecord={handleSelectRecord}
              onSelectAll={handleSelectAll}
            />
            {isFetchingPage && (
              <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/70 dark:bg-slate-900/70 z-10" aria-busy="true" aria-label="Loading page">
                <div className="flex flex-col items-center gap-2 px-4 py-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600 shadow-sm">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Loading page {currentPage}...</span>
                </div>
              </div>
            )}
          </div>

          {/* Pagination - show whenever there are records so user can change page size and see "Showing X–Y of Z" */}
          {(listViewData?.totalRecords ?? 0) > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          )}
        </>
      )}
      
      {/* Record Modal */}
      {isRecordModalOpen && (
        <GenericRecordModal
          objectApiName={objectApiName}
          objectId={objectId}
          objectLabel={objectLabel}
          isOpen={isRecordModalOpen}
          onClose={() => {
            setIsRecordModalOpen(false);
            setSelectedRecordId(undefined);
          }}
          recordId={selectedRecordId}
          onRecordCreated={() => {
            // Refetch the list view data when a record is successfully created
            refetchListViewData();
          }}
        />
      )}
      
      {/* Bulk Update Modal */}
      {showBulkUpdateModal && (
        <BulkUpdateModal
          objectLabel={objectLabel}
          selectedCount={selectedRecordIds.size}
          columns={listViewData?.listView.columns.filter(col => col.isVisible) || []}
          onClose={() => setShowBulkUpdateModal(false)}
          onConfirm={handleBulkUpdateConfirm}
        />
      )}
    </div>
  );
};

// Bulk Update Modal Component
interface BulkUpdateModalProps {
  objectLabel: string;
  selectedCount: number;
  columns: Array<{ fieldApiName: string; fieldLabel: string; fieldType: string }>;
  onClose: () => void;
  onConfirm: (fieldApiName: string, value: any) => void;
}

const BulkUpdateModal: React.FC<BulkUpdateModalProps> = ({
  objectLabel,
  selectedCount,
  columns,
  onClose,
  onConfirm
}) => {
  const [selectedField, setSelectedField] = useState('');
  const [updateValue, setUpdateValue] = useState('');
  
  const selectedColumn = columns.find(col => col.fieldApiName === selectedField);
  
  const handleSubmit = () => {
    if (!selectedField || updateValue === '') {
      return;
    }
    
    let processedValue: any = updateValue;
    
    // Convert value based on field type
    if (selectedColumn) {
      switch (selectedColumn.fieldType) {
        case 'number':
          processedValue = Number(updateValue);
          if (isNaN(processedValue)) {
            alert('Please enter a valid number');
            return;
          }
          break;
        case 'boolean':
          processedValue = updateValue.toLowerCase() === 'true' || updateValue === '1';
          break;
        default:
          processedValue = updateValue;
      }
    }
    
    onConfirm(selectedField, processedValue);
  };
  
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Bulk Update {selectedCount} {selectedCount === 1 ? 'Record' : 'Records'}
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Field to Update
              </label>
              <select
                value={selectedField}
                onChange={(e) => {
                  setSelectedField(e.target.value);
                  setUpdateValue('');
                }}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select field...</option>
                {columns.map(col => (
                  <option key={col.fieldApiName} value={col.fieldApiName}>
                    {col.fieldLabel}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedField && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Value
                </label>
                {selectedColumn?.fieldType === 'boolean' ? (
                  <select
                    value={updateValue}
                    onChange={(e) => setUpdateValue(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Select value...</option>
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                ) : (
                  <input
                    type={selectedColumn?.fieldType === 'number' ? 'number' : 'text'}
                    value={updateValue}
                    onChange={(e) => setUpdateValue(e.target.value)}
                    placeholder={`Enter ${selectedColumn?.fieldLabel.toLowerCase()}...`}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedField || updateValue === ''}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Update {selectedCount} {selectedCount === 1 ? 'Record' : 'Records'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

